import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2, Edit3, FileText, Download, Users, Trash2, Plus, FileCheck, Archive } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import { api } from '../../utils/api';
import { StatusBadge, ServiceIcon, LoadingSpinner } from '../common/Badges';
import { useLang } from '../../i18n';

const SERVICE_TYPES = ['electrical', 'cctv', 'networking', 'generator', 'intercom', 'access_control'];
const STATUSES = ['pending', 'assigned', 'in_progress', 'completed', 'cancelled'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

const emptyNew = () => ({
  customer_name: '', customer_email: '', customer_phone: '', customer_address: '',
  service_type: 'electrical', description: '', priority: 'medium', address: '',
  preferred_date: '', preferred_time: '', property_type: 'residential',
  price: '', price_notes: '', technician_ids: []
});

const AdminRequests = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [selectedTechIds, setSelectedTechIds] = useState([]);
  const [generatingInvoice, setGeneratingInvoice] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newReq, setNewReq] = useState(emptyNew());
  const [submittingNew, setSubmittingNew] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [requestsData, techsData, customersData] = await Promise.all([
        api('/api/service-requests'),
        api('/api/technicians'),
        api('/api/customers').catch(() => [])
      ]);
      setRequests(requestsData);
      setTechnicians(techsData);
      setCustomers(customersData);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  // ---- Edit (inline modal for any field) ----
  const openEdit = (req) => {
    setEditing({
      id: req.id,
      customer_name: req.customer_name || '',
      customer_email: req.customer_email || '',
      service_type: req.service_type,
      description: req.description || '',
      priority: req.priority || 'medium',
      address: req.address || '',
      preferred_date: req.preferred_date || '',
      preferred_time: req.preferred_time || '',
      status: req.status,
      price: req.price ?? '',
      price_notes: req.price_notes || '',
      notes: req.notes || '',
      ticket_number: req.ticket_number,
      assign_customer_id: req.customer_id || '',
      _orig_customer_id: req.customer_id || '',
    });
  };

  const saveEdit = async () => {
    try {
      const payload = { ...editing };
      delete payload.id; delete payload.ticket_number;
      delete payload.assign_customer_id; delete payload._orig_customer_id;
      if (payload.price === '' || payload.price == null) delete payload.price;
      else payload.price = parseFloat(payload.price);
      await api(`/api/service-requests/${editing.id}/edit`, { method: 'PUT', body: JSON.stringify(payload) });
      if (editing.assign_customer_id && editing.assign_customer_id !== editing._orig_customer_id) {
        await api(`/api/admin/requests/${editing.id}/assign-customer`, {
          method: 'PUT', body: JSON.stringify({ customer_id: editing.assign_customer_id })
        });
      }
      toast.success('Request updated');
      setEditing(null);
      loadData();
    } catch (err) { toast.error(err.message); }
  };

  // ---- Multi-tech assign ----
  const openAssign = (req) => {
    setAssignModal(req);
    setSelectedTechIds(req.assigned_technician_ids?.length ? [...req.assigned_technician_ids] : (req.assigned_technician_id ? [req.assigned_technician_id] : []));
  };
  const toggleTech = (id) => setSelectedTechIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const saveAssign = async () => {
    try {
      await api(`/api/service-requests/${assignModal.id}/assign-multi`, {
        method: 'PUT',
        body: JSON.stringify({ technician_ids: selectedTechIds })
      });
      toast.success(`${selectedTechIds.length} technician(s) assigned`);
      setAssignModal(null);
      loadData();
    } catch (err) { toast.error(err.message); }
  };

  // ---- Auto-generate invoice ----
  const generateInvoice = async (req) => {
    setGeneratingInvoice(req.id);
    try {
      const res = await api(`/api/service-requests/${req.id}/generate-invoice`, { method: 'POST' });
      if (res.existing) toast.info(`Invoice ${res.invoice_number} already exists`);
      else toast.success(`Invoice ${res.invoice_number} created`);
      navigate('/admin/invoices');
    } catch (err) { toast.error(err.message); }
    finally { setGeneratingInvoice(null); }
  };

  // ---- Save row as PDF ----
  const exportRowPDF = (req) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const w = pdf.internal.pageSize.getWidth();
    const margin = 14;
    pdf.setFillColor(10, 10, 10); pdf.rect(0, 0, w, 22, 'F');
    pdf.setTextColor(227, 27, 35); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(22);
    pdf.text('BELLAJA', margin, 13);
    pdf.setTextColor(220, 220, 220); pdf.setFontSize(8);
    pdf.text('Electrical & Security Systems', margin, 18);
    pdf.setTextColor(180, 180, 180); pdf.setFontSize(8);
    pdf.text('(203) 803-7035  |  info@bellajaelectric.com', w - margin, 13, { align: 'right' });
    pdf.text('NYC DOB Licensed  |  Fully Insured', w - margin, 18, { align: 'right' });

    pdf.setTextColor(20, 20, 20); pdf.setFontSize(16); pdf.setFont('helvetica', 'bold');
    pdf.text('SERVICE REQUEST', w / 2, 32, { align: 'center' });
    pdf.setDrawColor(227, 27, 35); pdf.setLineWidth(0.4);
    pdf.line(margin, 35, w - margin, 35);

    let y = 44;
    const row = (k, v) => {
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
      pdf.text(k, margin, y);
      pdf.setFont('helvetica', 'normal');
      const text = pdf.splitTextToSize(String(v ?? '-'), w - margin * 2 - 35);
      pdf.text(text, margin + 35, y);
      y += text.length * 4.5 + 1.5;
    };
    row('Ticket #', req.ticket_number);
    row('Customer', req.customer_name);
    row('Service', req.service_type.replace('_', ' ').toUpperCase());
    row('Priority', (req.priority || 'medium').toUpperCase());
    row('Status', (req.status || 'pending').toUpperCase());
    row('Address', req.address);
    row('Preferred Date', `${req.preferred_date || '-'} ${req.preferred_time || ''}`);
    row('Technician(s)', (req.assigned_technician_names?.length ? req.assigned_technician_names.join(', ') : (req.assigned_technician_name || 'Unassigned')));
    row('Price', req.price ? `$${req.price.toFixed(2)}` : '-');
    row('Price Notes', req.price_notes);
    row('Description', req.description);
    if (req.notes) row('Internal Notes', req.notes);

    y += 4;
    pdf.setFontSize(7); pdf.setTextColor(120, 120, 120);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, y);
    pdf.text('Confidential - For internal use', w - margin, y, { align: 'right' });

    pdf.save(`BELLAJA-Request-${req.ticket_number}.pdf`);
    toast.success('PDF downloaded');
  };

  const deleteRequest = async (id, ticket) => {
    if (!window.confirm(`Delete request "${ticket}"? This also removes its invoices and reports.`)) return;
    try { await api(`/api/service-requests/${id}`, { method: 'DELETE' }); toast.success('Deleted'); loadData(); }
    catch (err) { toast.error(err.message); }
  };

  // ---- Download consolidated project report (photos + reports + clock sessions) ----
  const downloadConsolidatedReport = async (req) => {
    try {
      const data = await api(`/api/admin/service-requests/${req.id}/consolidated-report`);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const w = pdf.internal.pageSize.getWidth();
      const h = pdf.internal.pageSize.getHeight();
      const m = 14;

      // Header band
      pdf.setFillColor(10, 10, 10); pdf.rect(0, 0, w, 22, 'F');
      pdf.setTextColor(227, 27, 35); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(22);
      pdf.text('BELLAJA', m, 13);
      pdf.setTextColor(220, 220, 220); pdf.setFontSize(8);
      pdf.text('Electrical & Security Systems', m, 18);
      pdf.setTextColor(180, 180, 180); pdf.setFontSize(8);
      pdf.text('(203) 803-7035  |  info@bellajaelectric.com', w - m, 13, { align: 'right' });
      pdf.text(new Date().toLocaleString(), w - m, 18, { align: 'right' });

      pdf.setTextColor(20, 20, 20); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(16);
      pdf.text('PROJECT CONSOLIDATION REPORT', w / 2, 32, { align: 'center' });
      pdf.setDrawColor(227, 27, 35); pdf.setLineWidth(0.4); pdf.line(m, 35, w - m, 35);

      let y = 44;
      const r = (k, v) => {
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.text(k, m, y);
        pdf.setFont('helvetica', 'normal');
        const lines = pdf.splitTextToSize(String(v ?? '-'), w - m * 2 - 38);
        pdf.text(lines, m + 38, y);
        y += lines.length * 4.5 + 1.5;
      };
      r('Ticket #', data.request.ticket_number);
      r('Customer', data.request.customer_name);
      r('Phone', data.request.customer_phone);
      r('Email', data.request.customer_email);
      r('Address', data.request.address);
      r('Service', (data.request.service_type || '').replace('_', ' ').toUpperCase());
      r('Status', (data.request.status || '').toUpperCase());
      r('Price', data.request.price ? `$${data.request.price.toFixed(2)}` : '-');
      r('Technicians', (data.request.assigned_technician_names || []).join(', ') || 'Unassigned');
      r('Created', data.request.created_at ? new Date(data.request.created_at).toLocaleString() : '-');
      r('Completed', data.request.completed_at ? new Date(data.request.completed_at).toLocaleString() : '-');
      r('Description', data.request.description);

      y += 4;
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.setTextColor(227, 27, 35);
      pdf.text('TOTALS', m, y); y += 5;
      pdf.setFontSize(9); pdf.setTextColor(20, 20, 20); pdf.setFont('helvetica', 'normal');
      r('Total Clock Hours', `${data.totals.clock_hours.toFixed(2)}h`);
      r('Reported Hours', `${data.totals.reported_hours.toFixed(2)}h`);
      r('Daily Reports', data.totals.report_count);
      r('Work Photos', data.totals.photo_count);
      r('Clock Sessions', data.totals.session_count);

      // Daily reports
      if (data.daily_reports?.length) {
        if (y > h - 40) { pdf.addPage(); y = 20; }
        y += 6;
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.setTextColor(227, 27, 35);
        pdf.text('DAILY REPORTS', m, y); y += 5;
        pdf.setFontSize(9); pdf.setTextColor(20, 20, 20);
        data.daily_reports.forEach((dr) => {
          if (y > h - 25) { pdf.addPage(); y = 20; }
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${dr.technician_name || 'Tech'} - ${dr.created_at ? new Date(dr.created_at).toLocaleString() : ''}`, m, y);
          y += 4;
          pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8);
          const notes = pdf.splitTextToSize(dr.notes || '-', w - m * 2);
          pdf.text(notes, m, y); y += notes.length * 4 + 2;
          pdf.setFontSize(9);
        });
      }

      // Clock sessions table
      if (data.clock_sessions?.length) {
        if (y > h - 40) { pdf.addPage(); y = 20; }
        y += 6;
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.setTextColor(227, 27, 35);
        pdf.text('CLOCK SESSIONS', m, y); y += 5;
        pdf.setFontSize(8); pdf.setTextColor(20, 20, 20); pdf.setFont('helvetica', 'normal');
        data.clock_sessions.forEach((s) => {
          if (y > h - 15) { pdf.addPage(); y = 20; }
          const line = `${s.technician_name} | ${s.clock_in ? new Date(s.clock_in).toLocaleString() : '-'} -> ${s.clock_out ? new Date(s.clock_out).toLocaleString() : 'ongoing'} (${(s.total_hours || 0).toFixed(2)}h)`;
          pdf.text(line, m, y); y += 4;
        });
      }

      // Photo URLs (the actual images would balloon PDF size; list URLs instead)
      if (data.work_photos?.length) {
        if (y > h - 40) { pdf.addPage(); y = 20; }
        y += 6;
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.setTextColor(227, 27, 35);
        pdf.text(`WORK PHOTOS (${data.work_photos.length})`, m, y); y += 5;
        pdf.setFontSize(8); pdf.setTextColor(40, 40, 200); pdf.setFont('helvetica', 'normal');
        data.work_photos.forEach((p, i) => {
          if (y > h - 15) { pdf.addPage(); y = 20; }
          pdf.text(`${i + 1}. ${p.caption || 'Photo'} - ${p.url}`, m, y);
          y += 4;
        });
      }

      pdf.setFontSize(7); pdf.setTextColor(120, 120, 120);
      pdf.text(`Generated ${new Date().toLocaleString()}  |  Confidential`, w / 2, h - 5, { align: 'center' });
      pdf.save(`BELLAJA-Report-${data.request.ticket_number}.pdf`);
      toast.success('Consolidated report downloaded');
    } catch (err) {
      toast.error('Failed to load report: ' + err.message);
    }
  };

  // ---- Download .zip project package (report.json + manifest + all photos) ----
  const downloadProjectPackage = async (req) => {
    try {
      toast.info('Preparing project package...');
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${API_URL}/api/admin/service-requests/${req.id}/download-package`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BELLAJA-${req.ticket_number}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Project package downloaded');
    } catch (err) {
      toast.error('Failed: ' + err.message);
    }
  };

  // Admin creates a new request (same form a customer fills)
  const submitNewRequest = async (e) => {
    e.preventDefault();
    if (!newReq.customer_name || !newReq.description || !newReq.address) {
      toast.error('Customer name, description and address are required');
      return;
    }
    setSubmittingNew(true);
    try {
      const payload = { ...newReq };
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      if (payload.price != null) payload.price = parseFloat(payload.price);
      if (!payload.technician_ids?.length) delete payload.technician_ids;
      const res = await api('/api/admin/service-requests', { method: 'POST', body: JSON.stringify(payload) });
      toast.success(`Created ${res.ticket_number}`);
      setCreating(false); setNewReq(emptyNew()); loadData();
    } catch (err) { toast.error(err.message); }
    finally { setSubmittingNew(false); }
  };
  const toggleNewTech = (id) => setNewReq(p => ({ ...p, technician_ids: p.technician_ids.includes(id) ? p.technician_ids.filter(x => x !== id) : [...p.technician_ids, id] }));

  if (loading) return <LoadingSpinner />;

  return (
    <div data-testid="admin-requests" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-red-500 font-bold tracking-[0.3em] mb-1">{t('requests').toUpperCase()}</p>
          <h1 className="font-heading text-3xl font-bold text-white">{t('all_requests')}</h1>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-xs text-gray-500">{requests.length} {t('total_count')}</p>
          <button onClick={() => setCreating(true)} className="btn-primary text-sm flex items-center gap-2" data-testid="new-request-btn">
            <Plus className="w-4 h-4" />{t('new_request')}
          </button>
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-800/50 text-[10px] uppercase tracking-wider text-gray-400">
            <tr>
              <th className="px-3 py-3 text-left">{t('ticket')}</th>
              <th className="px-3 py-3 text-left">{t('customer')}</th>
              <th className="px-3 py-3 text-left">{t('service_type')}</th>
              <th className="px-3 py-3 text-left">{t('status')}</th>
              <th className="px-3 py-3 text-left">{t('technicians')}</th>
              <th className="px-3 py-3 text-left">{t('price')}</th>
              <th className="px-3 py-3 text-left">{t('actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/50">
            {requests.map((r) => {
              const techs = r.assigned_technician_names?.length
                ? r.assigned_technician_names
                : (r.assigned_technician_name ? [r.assigned_technician_name] : []);
              return (
                <tr key={r.id} className="hover:bg-gray-800/30" data-testid={`admin-request-row-${r.id}`}>
                  <td className="px-3 py-3 font-mono text-xs text-gray-300">{r.ticket_number}</td>
                  <td className="px-3 py-3 text-white">{r.customer_name}</td>
                  <td className="px-3 py-3"><div className="flex items-center gap-2"><ServiceIcon type={r.service_type} className="w-4 h-4 text-gray-400" /><span className="capitalize text-xs">{r.service_type.replace('_', ' ')}</span></div></td>
                  <td className="px-3 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-3 py-3">
                    {techs.length === 0 ? (
                      <span className="text-xs text-gray-600">Unassigned</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {techs.map((n, i) => (
                          <span key={`${r.id}-tech-${i}-${n}`} className="text-[10px] bg-blue-900/40 text-blue-300 px-1.5 py-0.5 rounded">{n}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-green-400 font-bold text-xs">{r.price ? `$${r.price.toFixed(2)}` : '—'}</td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      <button onClick={() => openEdit(r)} className="text-[10px] bg-gray-800 hover:bg-gray-700 text-white px-2 py-1 rounded flex items-center gap-1" data-testid={`edit-request-btn-${r.id}`}><Edit3 className="w-3 h-3" />Edit</button>
                      <button onClick={() => openAssign(r)} className="text-[10px] bg-blue-900/40 hover:bg-blue-800/60 text-blue-300 px-2 py-1 rounded flex items-center gap-1" data-testid={`assign-multi-btn-${r.id}`}><Users className="w-3 h-3" />Assign</button>
                      <button onClick={() => generateInvoice(r)} disabled={generatingInvoice === r.id} className="text-[10px] bg-green-900/40 hover:bg-green-800/60 text-green-300 px-2 py-1 rounded flex items-center gap-1 disabled:opacity-40" data-testid={`gen-invoice-btn-${r.id}`}>
                        {generatingInvoice === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}Invoice
                      </button>
                      <button onClick={() => exportRowPDF(r)} className="text-[10px] bg-gray-800 hover:bg-gray-700 text-white px-2 py-1 rounded flex items-center gap-1" data-testid={`pdf-request-btn-${r.id}`}><Download className="w-3 h-3" />PDF</button>
                      <button onClick={() => downloadConsolidatedReport(r)} className="text-[10px] bg-purple-900/40 hover:bg-purple-800/60 text-purple-300 px-2 py-1 rounded flex items-center gap-1" data-testid={`consolidated-report-btn-${r.id}`} title="Project consolidation report"><FileCheck className="w-3 h-3" />Report</button>
                      <button onClick={() => downloadProjectPackage(r)} className="text-[10px] bg-cyan-900/40 hover:bg-cyan-800/60 text-cyan-300 px-2 py-1 rounded flex items-center gap-1" data-testid={`download-package-btn-${r.id}`} title="Download project package (.zip with photos + report)"><Archive className="w-3 h-3" />Zip</button>
                      <button onClick={() => deleteRequest(r.id, r.ticket_number)} className="text-[10px] bg-red-900/40 hover:bg-red-800/60 text-red-300 px-2 py-1 rounded flex items-center gap-1" data-testid={`delete-request-btn-${r.id}`}><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {requests.length === 0 && <div className="p-8 text-center text-gray-600 text-sm">No service requests yet.</div>}
      </div>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-gray-900 border border-gray-700 rounded-xl max-w-2xl w-full my-6" data-testid="edit-request-modal">
            <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
              <div>
                <p className="text-[10px] text-red-500 font-bold tracking-widest">EDIT</p>
                <h3 className="text-base font-bold text-white">{editing.ticket_number}</h3>
              </div>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-gray-800 rounded"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <Field label="Customer Name" value={editing.customer_name} onChange={(v) => setEditing({ ...editing, customer_name: v })} testid="edit-customer-name" />
              <Field label="Customer Email" value={editing.customer_email} onChange={(v) => setEditing({ ...editing, customer_email: v })} testid="edit-customer-email" />
              <div>
                <label className="text-[10px] text-gray-500 font-bold tracking-wider mb-1 block">ASSIGN TO CUSTOMER ACCOUNT</label>
                <select value={editing.assign_customer_id || ''} onChange={(e) => setEditing({ ...editing, assign_customer_id: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm" data-testid="edit-assign-customer">
                  <option value="">— not linked to any account —</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
                </select>
              </div>
              <SelectField label="Service Type" value={editing.service_type} onChange={(v) => setEditing({ ...editing, service_type: v })} options={SERVICE_TYPES} testid="edit-service-type" />
              <SelectField label="Status" value={editing.status} onChange={(v) => setEditing({ ...editing, status: v })} options={STATUSES} testid="edit-status" />
              <SelectField label="Priority" value={editing.priority} onChange={(v) => setEditing({ ...editing, priority: v })} options={PRIORITIES} testid="edit-priority" />
              <Field label="Price ($)" type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} testid="edit-price" />
              <Field label="Preferred Date" type="date" value={editing.preferred_date} onChange={(v) => setEditing({ ...editing, preferred_date: v })} testid="edit-preferred-date" />
              <Field label="Preferred Time" type="time" value={editing.preferred_time} onChange={(v) => setEditing({ ...editing, preferred_time: v })} testid="edit-preferred-time" />
              <div className="md:col-span-2"><Field label="Address" value={editing.address} onChange={(v) => setEditing({ ...editing, address: v })} testid="edit-address" /></div>
              <div className="md:col-span-2"><TextArea label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testid="edit-description" /></div>
              <div className="md:col-span-2"><TextArea label="Price Notes" value={editing.price_notes} onChange={(v) => setEditing({ ...editing, price_notes: v })} testid="edit-price-notes" /></div>
              <div className="md:col-span-2"><TextArea label="Internal Notes" value={editing.notes} onChange={(v) => setEditing({ ...editing, notes: v })} testid="edit-notes" /></div>
            </div>
            <div className="px-6 py-4 border-t border-gray-800 flex gap-2 justify-end">
              <button onClick={() => setEditing(null)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={saveEdit} className="btn-primary text-sm" data-testid="save-edit-btn">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Multi-Tech Assign Modal */}
      {assignModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setAssignModal(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-gray-900 border border-gray-700 rounded-xl max-w-md w-full" data-testid="assign-multi-modal">
            <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-base font-bold text-white">Assign Technicians</h3>
              <button onClick={() => setAssignModal(null)} className="p-1 hover:bg-gray-800 rounded"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-6">
              <p className="text-xs text-gray-400 mb-3">{assignModal.ticket_number} — select one or more</p>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {technicians.length === 0 ? (
                  <p className="text-center text-gray-500 text-sm py-4">No technicians available</p>
                ) : technicians.map((t) => (
                  <label key={t.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${selectedTechIds.includes(t.id) ? 'bg-red-900/20 border-red-500' : 'border-gray-800 hover:border-gray-700'}`} data-testid={`tech-checkbox-${t.id}`}>
                    <input type="checkbox" checked={selectedTechIds.includes(t.id)} onChange={() => toggleTech(t.id)} className="w-4 h-4 accent-red-500" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{t.name}</p>
                      <p className="text-xs text-gray-500">{t.email}</p>
                    </div>
                  </label>
                ))}
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setAssignModal(null)} className="btn-secondary text-sm">Cancel</button>
                <button onClick={saveAssign} className="btn-primary text-sm" data-testid="save-assign-btn">Assign ({selectedTechIds.length})</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Request Modal (admin creates same as customer) */}
      {creating && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={() => setCreating(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-gray-900 border border-gray-700 rounded-xl max-w-2xl w-full my-6" data-testid="new-request-modal">
            <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
              <div>
                <p className="text-[10px] text-red-500 font-bold tracking-widest">NEW REQUEST</p>
                <h3 className="text-base font-bold text-white">Create on customer's behalf</h3>
              </div>
              <button onClick={() => setCreating(false)} className="p-1 hover:bg-gray-800 rounded"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <form onSubmit={submitNewRequest} className="p-6 space-y-4">
              <div>
                <p className="text-[10px] text-red-500 font-bold tracking-widest mb-2">CUSTOMER DETAILS</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Customer Name *" value={newReq.customer_name} onChange={(v) => setNewReq({ ...newReq, customer_name: v })} testid="new-cust-name" />
                  <Field label="Customer Phone" type="tel" value={newReq.customer_phone} onChange={(v) => setNewReq({ ...newReq, customer_phone: v })} testid="new-cust-phone" />
                  <Field label="Customer Email" type="email" value={newReq.customer_email} onChange={(v) => setNewReq({ ...newReq, customer_email: v })} testid="new-cust-email" />
                  <Field label="Customer Home Address" value={newReq.customer_address} onChange={(v) => setNewReq({ ...newReq, customer_address: v })} testid="new-cust-address" />
                </div>
              </div>
              <div>
                <p className="text-[10px] text-red-500 font-bold tracking-widest mb-2">SERVICE DETAILS</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <SelectField label="Service Type *" value={newReq.service_type} onChange={(v) => setNewReq({ ...newReq, service_type: v })} options={SERVICE_TYPES} testid="new-service-type" />
                  <SelectField label="Property Type" value={newReq.property_type} onChange={(v) => setNewReq({ ...newReq, property_type: v })} options={['residential', 'commercial']} testid="new-prop-type" />
                  <SelectField label="Priority" value={newReq.priority} onChange={(v) => setNewReq({ ...newReq, priority: v })} options={PRIORITIES} testid="new-priority" />
                  <Field label="Estimated Price ($)" type="number" value={newReq.price} onChange={(v) => setNewReq({ ...newReq, price: v })} testid="new-price" />
                  <Field label="Preferred Date" type="date" value={newReq.preferred_date} onChange={(v) => setNewReq({ ...newReq, preferred_date: v })} testid="new-preferred-date" />
                  <Field label="Preferred Time" type="time" value={newReq.preferred_time} onChange={(v) => setNewReq({ ...newReq, preferred_time: v })} testid="new-preferred-time" />
                  <div className="md:col-span-2"><Field label="Job Address *" value={newReq.address} onChange={(v) => setNewReq({ ...newReq, address: v })} testid="new-address" /></div>
                  <div className="md:col-span-2"><TextArea label="Description *" value={newReq.description} onChange={(v) => setNewReq({ ...newReq, description: v })} testid="new-description" /></div>
                  <div className="md:col-span-2"><TextArea label="Price Notes" value={newReq.price_notes} onChange={(v) => setNewReq({ ...newReq, price_notes: v })} testid="new-price-notes" /></div>
                </div>
              </div>
              <div>
                <p className="text-[10px] text-red-500 font-bold tracking-widest mb-2">ASSIGN TECHNICIANS (optional, multi-select)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[180px] overflow-y-auto">
                  {technicians.length === 0 ? <p className="text-xs text-gray-500">No technicians</p> : technicians.map(t => (
                    <label key={t.id} className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-xs ${newReq.technician_ids.includes(t.id) ? 'bg-red-900/20 border-red-500' : 'border-gray-800 hover:border-gray-700'}`}>
                      <input type="checkbox" checked={newReq.technician_ids.includes(t.id)} onChange={() => toggleNewTech(t.id)} className="w-3.5 h-3.5 accent-red-500" data-testid={`new-tech-${t.id}`} />
                      <span className="text-white">{t.name}</span>
                      <span className="text-[9px] text-gray-500">({t.employment_type || 'W2'})</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-800">
                <button type="button" onClick={() => setCreating(false)} className="btn-secondary text-sm">Cancel</button>
                <button type="submit" disabled={submittingNew} className="btn-primary text-sm flex items-center gap-2" data-testid="submit-new-request">
                  {submittingNew ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Create Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', testid }) => (
  <div>
    <label className="text-[10px] text-gray-500 font-bold tracking-wider mb-1 block">{label}</label>
    <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm focus:border-red-500 focus:outline-none"
      data-testid={testid} />
  </div>
);
const TextArea = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-[10px] text-gray-500 font-bold tracking-wider mb-1 block">{label}</label>
    <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={2}
      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm focus:border-red-500 focus:outline-none"
      data-testid={testid} />
  </div>
);
const SelectField = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="text-[10px] text-gray-500 font-bold tracking-wider mb-1 block">{label}</label>
    <select value={value || ''} onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm focus:border-red-500 focus:outline-none"
      data-testid={testid}>
      {options.map((o) => <option key={o} value={o}>{o.replace('_', ' ').toUpperCase()}</option>)}
    </select>
  </div>
);

export default AdminRequests;
